import { createMonitoringAPICall } from "../../../utils/createMonitoringAPICall";
import { AxiosError, AxiosResponse } from "axios";
import { Dispatcher } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { NavigateFunction } from "react-router-dom";
import { addSeletedAndDetectionType } from "./addSeletedAndDetectionType";
import { handleAlertButtonsFill } from "./handleAlertButtonsFill";
import { timeSlicer } from "../timeSlicer";
import { getActiveIgnoreZones } from "./getActiveIgnoreZones";
import { getAlertsRefresh } from "./getAlertsRefresh";
import {
  APIErrorContext,
  universalAPIErrorHandler,
} from "../../../utils/universalAPIErrorHandler";
import { createLabels } from "./createLabels";
import { checkForRedirectAndFetchMP4 } from "./checkForRedirectAndFetchMP4";
import { alertService } from "../../../../services/api/alertService";
import { logger } from "../../../../utils/logger";
import { MutedAlertSites, MutedAlertCameras } from "./alertsInterfaces";
import { isAlertMuted, hideMutedFromView } from "./isAlertMuted";
import {
  clearAlertsFetchError,
  recordAlertsFetchError,
} from "../AlertsStore/alertsFetchErrorStore";
import { recordGroupAlertSnapshot } from "../AlertsStore/alertsScopeStore";

const isCurrentDate = (inputDate: string): boolean => {
  const currentDate = new Date();
  const formattedCurrentDate = currentDate.toISOString().slice(0, 10); // Get the current date in YYYY-MM-DD format

  return inputDate === formattedCurrentDate;
};

export const numberListToString = (numbers: number[]): string =>
  numbers.join(",");

export const getAlertsWithQuerry = (
  setAlertData: Dispatcher<any>,
  navigate: NavigateFunction,
  setSelectedRow: Dispatcher<any>,
  setFetchingFilteredData: Dispatcher<boolean>,
  setSeeMoreURL: Dispatcher<string>,
  setMP4URL: Dispatcher<string>,
  groupId: string,
  siteId: number[],
  cameraId: number[],
  startDate: string,
  endDate: string,
  startTime: Date,
  endTime: Date,
  filterType: string,
  setSelectedButtonsFilled: any,
  setThumbnailVideos: any,
  setDateOutsideRange: Dispatcher<boolean>,
  setPage: Dispatcher<number>,
  setVideoClipLoading: Dispatcher<boolean>,
  setAlertsToDisplay: Dispatcher<boolean>,
  setPageLoading: Dispatcher<boolean>,
  setSelectedAlertCameraInfo: any,
  setIgnoreZonesSelectedAlert: any,
  setAlertsIndexOrder: any,
  setRefreshInterval: any,
  setCurrentName: any,
  setUserFilters: any,
  setRefreshingData: any,
  pageLoadedNumber: any,
  refreshInterval: number,
  userFilters: any,
  cameraOptions: any,
  siteOptions: any,
  setRefreshingToken: Dispatcher<boolean>,
  refreshingToken: boolean,
  setAlertLabels: Dispatcher<string[]>,
  setPeriodHours: Dispatcher<any>,
  setQueryLimitHit: Dispatcher<boolean>,
  setForwardArrowDisabled: Dispatcher<boolean>,
  setBackArrowDisabled: Dispatcher<boolean>,
  setShowRefreshButton: Dispatcher<boolean>,
  setRowsSortable: Dispatcher<boolean>,
  setAlertsRefreshing: Dispatcher<boolean>,
  getCurrentSelectedRowKey: () => string | null,
  mutedAlertSites: MutedAlertSites,
  mutedAlertCameras: MutedAlertCameras,
  setVideoProcessing?: Dispatcher<boolean>
) => {
  const AxiosUI = createMonitoringAPICall(``);
  const useAsyncGeneration = !userFilters?.has_video;
  const retryFunction = () => {
    getAlertsWithQuerry(
      setAlertData,
      navigate,
      setSelectedRow,
      setFetchingFilteredData,
      setSeeMoreURL,
      setMP4URL,
      groupId,
      siteId,
      cameraId,
      startDate,
      endDate,
      startTime,
      endTime,
      filterType,
      setSelectedButtonsFilled,
      setThumbnailVideos,
      setDateOutsideRange,
      setPage,
      setVideoClipLoading,
      setAlertsToDisplay,
      setPageLoading,
      setSelectedAlertCameraInfo,
      setIgnoreZonesSelectedAlert,
      setAlertsIndexOrder,
      setRefreshInterval,
      setCurrentName,
      setUserFilters,
      setRefreshingData,
      pageLoadedNumber,
      refreshInterval,
      userFilters,
      cameraOptions,
      siteOptions,
      setRefreshingToken,
      refreshingToken,
      setAlertLabels,
      setPeriodHours,
      setQueryLimitHit,
      setForwardArrowDisabled,
      setBackArrowDisabled,
      setShowRefreshButton,
      setRowsSortable,
      setAlertsRefreshing,
      getCurrentSelectedRowKey,
      mutedAlertSites,
      mutedAlertCameras,
      setVideoProcessing
    );
  };

  let searchParamsString = ``;

  if (startDate) {
    searchParamsString += `&start=${startDate}`;
  }

  if (endDate) {
    searchParamsString += `&end=${endDate}`;
  }

  if (groupId) {
    if (groupId !== "undefined") {
      searchParamsString += `&group_id=${groupId}`;
    }
  }

  if (Array.isArray(siteId) && siteId.length > 0) {
    searchParamsString += `&customer_id=${numberListToString(siteId)}`;
  }

  if (Array.isArray(cameraId) && cameraId.length > 0) {
    searchParamsString += `&camera_id=${numberListToString(cameraId)}`;
  }

  if (startDate.toString() === endDate.toString() && startTime !== null) {
    searchParamsString += `&start_time=${timeSlicer(startTime)}`;
  }

  if (startDate.toString() === endDate.toString() && endTime !== null) {
    searchParamsString += `&end_time=${timeSlicer(endTime)}`;
  }

  if (userFilters.has_video) {
    searchParamsString += `&has_video=true`;
  }

  localStorage.setItem(
    "queryString",
    JSON.stringify(`${filterType}${searchParamsString}`)
  );

  // Make getAlertsFunction async directly
  const getAlertsFunction = async (refreshInt: number) => {
    // Remove the inner async () => { ... } wrapper
    clearAlertsFetchError();
    try {
      const responseData = await alertService.getAlerts({
        filterType,
        searchParamsString,
      });

      // Hide muted alerts from the live (Unresolved) queue only — the
      // Resolved queue retains them, including for the muting user.
      const filteredRecords = hideMutedFromView(filterType)
        ? responseData.records.filter((row: any) => {
            return !isAlertMuted(row, mutedAlertSites, mutedAlertCameras);
          })
        : responseData.records;

      // Update responseData.records with filtered data
      // responseData.records = filteredRecords;

      recordGroupAlertSnapshot(
        responseData,
        groupId,
        filterType,
        searchParamsString
      );

      if (responseData.parameters) {
        if (responseData.parameters.period_hours) {
          setPeriodHours(responseData.parameters.period_hours);
        }
      }
      setRowsSortable(true);
      if (filteredRecords[0]) {
        if (
          startDate &&
          endDate &&
          !siteId &&
          !cameraId &&
          responseData.parameters.query_limit
        ) {
          if (responseData.records.length > 99) {
            setQueryLimitHit(true);
          } else setQueryLimitHit(false);
        }
        setSelectedRow(filteredRecords[0]);
        handleAlertButtonsFill(filteredRecords[0], setSelectedButtonsFilled);
        localStorage.setItem(
          "selectedRowAlerts",
          JSON.stringify(filteredRecords[0])
        );
        filteredRecords[0].camera_pk &&
          getActiveIgnoreZones(
            filteredRecords[0].camera_pk,
            setSelectedAlertCameraInfo,
            setIgnoreZonesSelectedAlert,
            navigate
          );
      }
      if (responseData.parameters?.period_hours) {
        setAlertData(
          addSeletedAndDetectionType(
            responseData.records,
            setAlertsIndexOrder,
            hideMutedFromView(filterType) ? mutedAlertSites : [],
            hideMutedFromView(filterType) ? mutedAlertCameras : []
          )
        );
      }
      if (filteredRecords[0]) {
        checkForRedirectAndFetchMP4(
          filteredRecords[0],
          navigate,
          setMP4URL,
          setVideoClipLoading,
          setShowRefreshButton,
          false,
          getCurrentSelectedRowKey,
          setVideoProcessing,
          useAsyncGeneration
        );
        setAlertsToDisplay(true);
        setPage(0);
        setSeeMoreURL(filteredRecords[0].alert_url);
        if (filteredRecords.length === 1) {
          setForwardArrowDisabled(true);
          setBackArrowDisabled(true);
        }
        if (filteredRecords.length > 1) {
          setForwardArrowDisabled(false);
          setBackArrowDisabled(true);
        }
        setAlertsToDisplay(true);
        setFetchingFilteredData(false);
        setPageLoading(false);
        createLabels(responseData.records, setAlertLabels);
      }
      if (!filteredRecords[0]) {
        setAlertsToDisplay(false);
        setPageLoading(false);
        setFetchingFilteredData(false);
      }
      if (
        localStorage.tokenRefreshing === "false" ||
        localStorage.tokenRefreshing === undefined
      ) {
        let dataRecordTimeStamp = "";
        if (filteredRecords[0]) {
          dataRecordTimeStamp = filteredRecords[0].approx_capture_timestamp;
        }
        if (
          ((!startDate || startDate === "") && (!endDate || endDate === "")) ||
          (startDate &&
            startDate !== "" &&
            !endDate &&
            isCurrentDate(startDate)) ||
          (endDate && endDate !== "" && !startDate && isCurrentDate(endDate)) ||
          (startDate &&
            startDate !== "" &&
            endDate &&
            endDate !== "" &&
            isCurrentDate(startDate) &&
            isCurrentDate(endDate))
        ) {
          if (!endTime) {
            setRowsSortable(false);
            getAlertsRefresh(
              setAlertData,
              navigate,
              groupId,
              siteId,
              cameraId,
              startDate,
              endDate,
              startTime,
              endTime,
              filterType,
              setSelectedRow,
              setRefreshingData,
              setSelectedAlertCameraInfo,
              setIgnoreZonesSelectedAlert,
              setAlertsIndexOrder,
              refreshInt,
              setRefreshInterval,
              pageLoadedNumber,
              setSeeMoreURL,
              setMP4URL,
              setVideoClipLoading,
              setSelectedButtonsFilled,
              setAlertsToDisplay,
              setCurrentName,
              setPage,
              setDateOutsideRange,
              setFetchingFilteredData,
              setPageLoading,
              setUserFilters,
              userFilters,
              cameraOptions,
              siteOptions,
              dataRecordTimeStamp,
              setRefreshingToken,
              refreshingToken,
              responseData.records.length,
              setForwardArrowDisabled,
              setBackArrowDisabled,
              setShowRefreshButton,
              setAlertsRefreshing,
              false,
              getCurrentSelectedRowKey,
              setVideoProcessing,
              useAsyncGeneration
            );
          } else {
            setAlertsRefreshing(false);
          }
        } else {
          setAlertsRefreshing(false);
        }
      }
      localStorage.setItem("newQueryRunning", "false");
    } catch (error) {
      // Handle error
      setPage(0);
      universalAPIErrorHandler(
        error as AxiosError,
        navigate,
        retryFunction,
        (context?: APIErrorContext) => {
          // Surface the failure (SUP-445): without this a 400/5xx from the
          // backend rendered as "No alerts to display".
          recordAlertsFetchError(error, context);
          setAlertsToDisplay(false);
          setPageLoading(false);
          setFetchingFilteredData(false);
        }
      );
    }
  };

  if (groupId) {
    AxiosUI.get(`group/${groupId}`).then(
      (response: AxiosResponse) => {
        if (response.data.refresh_interval_seconds) {
          getAlertsFunction(response.data.refresh_interval_seconds * 1000);
        } else {
          getAlertsFunction(refreshInterval);
        }
      },
      (reason: AxiosError) => {
        logger.log("error reason:", reason.message);
        getAlertsFunction(refreshInterval);
      }
    );
  } else {
    getAlertsFunction(refreshInterval);
  }
};
